/**
 * session.ts — renovação do access token.
 *
 * Faz a chamada de `/api/auth/refresh` na mão, sem passar por `http.ts`, por dois
 * motivos: evita a dependência circular (http.ts precisa renovar, e renovar precisaria
 * de http.ts) e mantém a renovação fora da política de retry/401 do cliente HTTP —
 * uma renovação recusada precisa falhar na hora, não ser tentada de novo.
 *
 * Single-flight: várias requests podem tomar 401 quase ao mesmo tempo. Todas aguardam
 * a mesma promessa, então a API recebe uma única renovação e só um refresh token é
 * rotacionado. Entre abas diferentes isso não se aplica — quem resolve a corrida lá é
 * a janela de tolerância de rotação no backend.
 */

import { CapacitorHttp } from "@capacitor/core"
import { API_BASE_URL } from "@/lib/apiConfig"
import { token } from "@/lib/tokenStore"
import {
  NATIVE_PLATFORM,
  PLATFORM_HEADER,
  REFRESH_TOKEN_HEADER,
  isNativeApp,
} from "@/lib/platform"
import type { ApiResponse } from "@/types/api"
import type { AuthResponse, UserDto } from "@/types/auth"

const REFRESH_PATH = "/api/auth/refresh"
const REFRESH_TIMEOUT_MS = 15_000

/**
 * - `renewed`: há um access token novo em mãos.
 * - `rejected`: a API recusou o refresh token; a sessão acabou e é preciso logar.
 * - `unavailable`: não deu para saber (rede caiu, 5xx, throttle). A sessão pode muito
 *   bem estar viva, então ninguém deve ser deslogado por causa disso.
 */
export type RefreshOutcome =
  | { status: "renewed"; accessToken: string; user: UserDto }
  | { status: "rejected" }
  | { status: "unavailable" }

let inFlight: Promise<RefreshOutcome> | null = null

/**
 * Renova a sessão, reaproveitando a chamada já em andamento se houver.
 *
 * Nunca lança: toda falha vira `rejected` ou `unavailable`, porque isso é chamado de
 * dentro de interceptadores onde uma exceção solta mascararia o erro original.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  inFlight ??= performRefresh().finally(() => {
    inFlight = null
  })

  return inFlight
}

/**
 * Retoma a sessão ao abrir o app, quando ainda não há access token em memória.
 *
 * Só vai à rede se houver indício de sessão neste dispositivo. Sem isso, todo visitante
 * anônimo abrindo a tela de login gastaria uma chamada de refresh fadada ao 401 — e
 * ainda consumiria a cota de rate limit de quem realmente precisa renovar.
 */
export async function resumeSession(): Promise<RefreshOutcome> {
  if (isNativeApp()) {
    const stored = await token.getRefreshToken()
    if (!stored) return { status: "rejected" }
  } else if (!token.hasSessionHint()) {
    return { status: "rejected" }
  }

  return refreshSession()
}

async function performRefresh(): Promise<RefreshOutcome> {
  if (typeof window === "undefined") return { status: "rejected" }

  try {
    const { status, payload } = isNativeApp()
      ? await refreshNative()
      : await refreshWeb()

    if (status === 401 || status === 403) {
      // A API foi explícita: esse refresh token não vale mais. Apagar o indício evita
      // que a próxima abertura repita a chamada, e cada repetição de um token já
      // rotacionado é exatamente o que a detecção de reuso trata como roubo.
      endSession()
      return { status: "rejected" }
    }

    const data = payload?.data

    if (status < 200 || status >= 300 || payload?.success === false || !data?.accessToken) {
      return { status: "unavailable" }
    }

    token.set(data.accessToken, data.expiresIn)
    await token.saveRefreshToken(data.refreshToken)

    return { status: "renewed", accessToken: data.accessToken, user: data.user }
  } catch {
    // Rede fora, DNS, timeout: a sessão continua possivelmente válida.
    return { status: "unavailable" }
  }
}

async function refreshWeb(): Promise<{ status: number; payload?: ApiResponse<AuthResponse> }> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)

  try {
    const response = await fetch(`${API_BASE_URL}${REFRESH_PATH}`, {
      method: "POST",
      // Sem isso o cookie HttpOnly não acompanha a request cross-origin e todo
      // refresh no browser voltaria 401.
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
    })

    const text = await response.text()
    return { status: response.status, payload: parsePayload(text) }
  } finally {
    clearTimeout(timer)
  }
}

async function refreshNative(): Promise<{ status: number; payload?: ApiResponse<AuthResponse> }> {
  const stored = await token.getRefreshToken()

  if (!stored) return { status: 401 }

  const response = await CapacitorHttp.request({
    url: `${API_BASE_URL}${REFRESH_PATH}`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      [PLATFORM_HEADER]: NATIVE_PLATFORM,
      [REFRESH_TOKEN_HEADER]: stored,
    },
    responseType: "json",
    connectTimeout: REFRESH_TIMEOUT_MS,
    readTimeout: REFRESH_TIMEOUT_MS,
  })

  return {
    status: response.status,
    payload: typeof response.data === "string"
      ? parsePayload(response.data)
      : (response.data as ApiResponse<AuthResponse> | undefined),
  }
}

function parsePayload(text: string): ApiResponse<AuthResponse> | undefined {
  if (!text) return undefined
  try {
    return JSON.parse(text) as ApiResponse<AuthResponse>
  } catch {
    // Resposta sem JSON (ex.: 502/503 do Azure) — tratada como indisponibilidade.
    return undefined
  }
}

/** Descarta tudo o que representa a sessão local. */
export function endSession(): void {
  token.clear()
  token.clearSessionHint()
}

/**
 * Access token válido para requests que não passam pelo `http.ts` — uploads
 * multipart, download de blobs e a negociação do SignalR.
 *
 * Esses caminhos não têm o tratamento automático de 401, então a renovação precisa
 * acontecer antes: um token vencido ali viraria um erro visto pelo usuário (uma foto
 * que não carrega, um upload que falha) em vez de uma repetição silenciosa.
 *
 * @param renewWithinSeconds margem para considerar o token velho demais para ser usado.
 */
export async function currentAccessToken(renewWithinSeconds = 30): Promise<string | null> {
  if (!token.get()) {
    const outcome = await resumeSession()
    return outcome.status === "renewed" ? outcome.accessToken : null
  }

  if (token.isExpiringSoon(renewWithinSeconds)) {
    await refreshSession()
  }

  return token.get()
}
